import { useEffect, useState } from 'react';

import { DesignPage } from '../services/designService';
import type { UserFlow } from '../store/services/userFlowApi';

import type { DesignSubTab } from './DesignPanel';
import { normalizeDesignNavigationTarget } from './designPreviewUtils';

/* ─── Navbar broadcast data shapes ─── */

export interface PageInfo {
  id: string;
  name: string;
  version: number;
  failed: boolean;
  generating: boolean;
  sections_done: number;
  sections_total: number;
  generation_phase: string;
  planned?: boolean;
  done?: boolean;
  /** Preview content exists even when generation/quality review is unfinished. */
  hasPreview?: boolean;
  publicReady?: boolean;
}

export interface FlowInfo {
  id: string;
  persona: string;
  version: number;
}

/* ─── Download helpers ─── */
export function downloadFile(filename: string, content: string) {
  // eslint-disable-next-line no-undef
  const blob = new Blob([content], { type: 'text/jsx' });
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement('a'), { href: url, download: filename });

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function downloadSinglePage(page: DesignPage) {
  downloadFile(`${page.page_name.replace(/[^a-zA-Z0-9_-]/g, '_')}.jsx`, page.jsx_content);
}

/* ─── Mermaid helpers ─── */
export function sanitizeMermaidId(prefix: string, label: string): string {
  return `${prefix}-${label.replace(/[^a-zA-Z0-9-]/g, '').toLowerCase()}-${Date.now()}`;
}

export interface ParsedDesignPath {
  subTab: DesignSubTab;
  page: string | null;
  pageId: string | null;
}

/** Parse a deep-link path like "pages/Landing", "pages/id/<id>", or "useflow". */
// eslint-disable-next-line max-statements
export function parseDesignPath(path: string | null | undefined): ParsedDesignPath {
  if (!path) return { subTab: 'pages', page: null, pageId: null };
  if (path === 'useflow' || path === 'userflow') {
    return { subTab: 'user-flow', page: null, pageId: null };
  }
  if (path.startsWith('pages/id/')) {
    const encodedPageId = path.slice('pages/id/'.length);

    try {
      return {
        subTab: 'pages', page: null,
        pageId: encodedPageId ? decodeURIComponent(encodedPageId) : null,
      };
    } catch {
      return { subTab: 'pages', page: null, pageId: null };
    }
  }
  if (path.startsWith('pages/')) {
    const encodedPage = path.slice('pages/'.length);

    try {
      return {
        subTab: 'pages', page: encodedPage ? decodeURIComponent(encodedPage) : null, pageId: null,
      };
    } catch {
      return { subTab: 'pages', page: null, pageId: null };
    }
  }

  return { subTab: 'pages', page: null, pageId: null };
}

/** Resolve stable page-ID links and legacy page-name links to the current name. */
export function resolveDesignPageTarget(
  target: Pick<ParsedDesignPath, 'page' | 'pageId'>,
  pages: Array<Pick<DesignPage, 'id' | 'page_name'>>,
): string | null {
  if (target.pageId) {
    return pages.find((page) => page.id === target.pageId)?.page_name ?? null;
  }
  if (!target.page || pages.length === 0) return target.page;
  if (pages.some((page) => page.page_name === target.page)) return target.page;
  const normalized = target.page.toLowerCase();

  return pages.find((page) => page.page_name.toLowerCase() === normalized)?.page_name ?? null;
}

/** Check a page deep link only after page metadata is available. */
export function isDesignPageRouteUnavailable(
  path: string | null | undefined,
  pages: Array<Pick<DesignPage, 'id' | 'page_name'>>,
  loading: boolean,
): boolean {
  if (loading || !path?.startsWith('pages/')) return false;
  const target = parseDesignPath(path);

  if (!target.page && !target.pageId) return true;

  return resolveDesignPageTarget(target, pages) === null;
}

export function normalizePageKey(name: string): string {
  return name.toLowerCase().replace(/["'`\s_-]/g, '');
}

/** Strip Mermaid label quoting (`A["Page: action"]`) and stray wrapper quotes. */
const unquoteMermaidLabel = (raw: string): string => (
  raw.trim().replace(/^["'`]+/, '').replace(/["'`]+$/, '').trim()
);

/** Normalize browser-computed rgb/hex colors for the native color picker. */
// eslint-disable-next-line max-statements
export function toColorPickerValue(value: string, fallback = '#000000'): string {
  const normalized = value.trim();
  const hex = normalized.match(/^#([\da-f]{3}|[\da-f]{6}|[\da-f]{8})$/i)?.[1];

  if (hex) {
    if (hex.length === 3) return `#${hex.split('').map((part) => `${part}${part}`).join('')}`.toLowerCase();

    return `#${hex.slice(0, 6)}`.toLowerCase();
  }
  const rgb = normalized.match(
    /^rgba?\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)(?:\s*,\s*(\d*\.?\d+))?/i,
  );

  if (!rgb) return fallback;
  if (rgb[4] !== undefined && Number(rgb[4]) === 0) return fallback;
  const channel = (part: string) => Math.max(0, Math.min(255, Math.round(Number(part))))
    .toString(16).padStart(2, '0');

  return `#${channel(rgb[1])}${channel(rgb[2])}${channel(rgb[3])}`;
}

const EDITOR_COLOR_PROPERTIES = new Set([
  'color',
  'backgroundColor',
  'borderColor',
  'outlineColor',
]);

/** Return whether an inspector value is only a browser-computed normalization. */
export function editorStyleValuesEquivalent(
  property: string,
  currentValue: string,
  nextValue: string,
): boolean {
  const current = currentValue.trim();
  const next = nextValue.trim();

  if (current === next) return true;
  if (property === 'textAlign') {
    const normalize = (value: string) => (value === 'start' ? 'left' : (value === 'end' ? 'right' : value));

    return normalize(current) === normalize(next);
  }
  if (EDITOR_COLOR_PROPERTIES.has(property)) {
    const colorPattern = /^(?:#[\da-f]{3,8}|rgba?\([^)]*\))$/i;
    const hasTransparency = (value: string) => {
      const alpha = value.match(/^rgba\([^,]+,[^,]+,[^,]+,\s*(\d*\.?\d+)\s*\)$/i)?.[1];

      return alpha !== undefined && Number(alpha) !== 1;
    };

    return colorPattern.test(current)
      && colorPattern.test(next)
      && !hasTransparency(current)
      && !hasTransparency(next)
      && toColorPickerValue(current) === toColorPickerValue(next);
  }

  return false;
}

export function isPlannedDesignPage(page: DesignPage): boolean {
  return !!page.planned || page.id.startsWith('virtual:') || page.id.startsWith('planned-')
    || (page.generation_phase === 'planned' && !page.jsx_content && !page.has_jsx_content);
}

function hasMissingSections(page: DesignPage): boolean {
  if (Array.isArray(page.missing_sections_json)) return page.missing_sections_json.length > 0;

  return Boolean(page.missing_sections_json);
}

export function isReadyDesignPage(page: DesignPage): boolean {
  const hasRenderableContent = !!page.compiled_html || !!page.has_compiled_html
    || !!page.jsx_content || !!page.has_jsx_content;
  const total = page.sections_total || 0;
  const sectionsComplete = total === 0 || (page.sections_done || 0) >= total;

  return hasRenderableContent
    && !isPlannedDesignPage(page)
    && !page.generation_in_progress
    && !page.generation_failed
    && !page.credit_blocked
    && !hasMissingSections(page)
    && sectionsComplete;
}

/* ─── Broadcast page/flow data + active selection to navbar ─── */

function toPageInfo(p: DesignPage): PageInfo {
  const gen = !!p.generation_in_progress;
  const hasContent = !!p.compiled_html || !!p.has_compiled_html || !!p.has_jsx_content;
  const planned = isPlannedDesignPage(p);
  const done = hasContent && !gen && !p.generation_failed && !p.credit_blocked;

  return {
    id: p.id, name: p.page_name, version: p.version,
    failed: !!p.generation_failed, generating: gen,
    sections_done: p.sections_done || 0,
    sections_total: p.sections_total || 0,
    generation_phase: p.generation_phase || '',
    planned,
    done: planned ? false : done,
    hasPreview: hasContent,
    publicReady: isReadyDesignPage(p),
  };
}

function toFlowInfo(f: UserFlow): FlowInfo {
  return { id: f.id, persona: f.persona, version: f.version };
}

// Parse unique page names from the mermaid content of user flows.
// Node labels use the "PageName: Action" convention — we keep the PageName.
export function extractPagesFromFlows(flows: UserFlow[]): string[] {
  const labelRe = /\[([^\]]+)\]/g;
  const seen = new Set<string>();
  const out: string[] = [];
  for (const f of flows) {
    const src = f.mermaid_content || '';
    let m: RegExpExecArray | null;
    while ((m = labelRe.exec(src)) !== null) {
      // Server-rendered flows quote labels: `A["Page: action"]`. Drop the
      // quotes before splitting so a planned page never appears as `"Page`
      // next to the real `Page` once it is generated.
      const raw = unquoteMermaidLabel(m[1]);
      const name = unquoteMermaidLabel(raw.includes(':') ? raw.split(':')[0] : raw);
      if (!name) continue;
      const key = normalizePageKey(name);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(name);
    }
  }
  return out;
}

function mergePlannedPages(pages: PageInfo[], flows: UserFlow[]): PageInfo[] {
  const existingKeys = new Set(pages.map((p) => normalizePageKey(p.name)));
  const planned: PageInfo[] = extractPagesFromFlows(flows)
    .filter((n) => !existingKeys.has(normalizePageKey(n)))
    .map((n) => ({
      id: `planned-${normalizePageKey(n)}`,
      name: n, version: 0,
      failed: false, generating: false,
      sections_done: 0, sections_total: 0,
      generation_phase: '', planned: true, publicReady: false,
    }));
  return [...pages, ...planned];
}

export function useNavbarBroadcast(
  mode: DesignSubTab | undefined,
  designPages: DesignPage[],
  userFlows: UserFlow[],
  activePage: string | null,
  activePersona: string | null,
  onIframeNavigate?: (pageName: string) => void,
  readyOnly = false,
) {
  // Always broadcast page data so the navbar cumulative progress bar works
  // even when the Design tab is collapsed or a different sub-tab is active.
  // Planned pages extracted from user-flow nodes are appended so the sidebar
  // lists every page implied by the flow, not just those already generated.
  useEffect(() => {
    const pageInfo = designPages.map(toPageInfo);
    const merged = readyOnly ? pageInfo : mergePlannedPages(pageInfo, userFlows);

    window.dispatchEvent(new globalThis.CustomEvent('design_pages_data', { detail: merged }));
  }, [designPages, userFlows, readyOnly]);

  useEffect(() => {
    if (mode === 'pages' && activePage) {
      window.dispatchEvent(new globalThis.CustomEvent('design_page_changed', { detail: activePage }));
    }
  }, [mode, activePage]);

  useEffect(() => {
    if (mode === 'user-flow') {
      window.dispatchEvent(new globalThis.CustomEvent('user_flows_data', { detail: userFlows.map(toFlowInfo) }));
    }
  }, [mode, userFlows]);

  useEffect(() => {
    if (mode === 'user-flow' && activePersona) {
      window.dispatchEvent(new globalThis.CustomEvent('user_flow_changed', { detail: activePersona }));
    }
  }, [mode, activePersona]);

  // Iframe link-click interception: navigate to matching design page
  useIframeLinkNavigation(designPages, onIframeNavigate);
}

/* ─── Navbar three-dot action hooks ─── */

export function usePageNavbarActions(
  pages: DesignPage[], onDelete: (id: string, name: string) => void,
  setShareOpen: (v: boolean) => void, setFullscreen: (v: boolean) => void,
  onVersions?: (pageId: string, pageName: string) => void,
) {
  useEffect(() => {
    const h = () => setShareOpen(true);

    window.addEventListener('open_share_modal', h);

    return () => window.removeEventListener('open_share_modal', h);
  }, [setShareOpen]);

  useEffect(() => {
    const h = () => setFullscreen(true);

    window.addEventListener('open_design_fullscreen', h);

    return () => window.removeEventListener('open_design_fullscreen', h);
  }, [setFullscreen]);

  useEffect(() => {
    const h = (e: Event) => {
      const d = (e as globalThis.CustomEvent).detail;

      if (d.action === 'download') {
        const page = pages.find((p) => p.page_name === d.name);

        if (page) downloadSinglePage(page);
      } else if (d.action === 'delete' && d.id) {
        onDelete(d.id, d.name);
      } else if (d.action === 'versions' && d.id && onVersions) {
        onVersions(d.id, d.name);
      }
    };

    window.addEventListener('navbar_page_action', h);

    return () => window.removeEventListener('navbar_page_action', h);
  }, [pages, onDelete, onVersions]);
}

export function useFlowNavbarActions(
  flows: UserFlow[], onDelete: (id: string, p: string) => void,
  onVersions?: (flowId: string, persona: string) => void,
) {
  useEffect(() => {
    const h = (e: Event) => {
      const d = (e as globalThis.CustomEvent).detail;

      if (d.action === 'download') {
        const flow = flows.find((f) => f.persona === d.persona);

        if (flow) downloadFile(`${d.persona.replace(/[^a-zA-Z0-9_-]/g, '_')}_flow.md`, flow.mermaid_content);
      } else if (d.action === 'delete' && d.id) {
        onDelete(d.id, d.persona);
      } else if (d.action === 'versions' && d.id && onVersions) {
        onVersions(d.id, d.persona);
      }
    };

    window.addEventListener('navbar_flow_action', h);

    return () => window.removeEventListener('navbar_flow_action', h);
  }, [flows, onDelete, onVersions]);
}

export function useVersionPreview(pageName: string) {
  const [override, setOverride] = useState<{ compiled_html: string; version: number } | null>(null);

  useEffect(() => {
    const onPreview = (e: Event) => {
      const d = (e as globalThis.CustomEvent).detail;

      if (d.pageName === pageName) setOverride({ compiled_html: d.compiled_html, version: d.version });
    };
    const onClear = () => setOverride(null);

    window.addEventListener('preview_version', onPreview);
    window.addEventListener('preview_version_clear', onClear);

    return () => { window.removeEventListener('preview_version', onPreview); window.removeEventListener('preview_version_clear', onClear); };
  }, [pageName]);

  return override;
}

/* ─── Iframe link-click interception ─── */
export function useIframeLinkNavigation(
  designPages: DesignPage[],
  onNavigate?: (pageName: string) => void,
) {
  useEffect(() => {
    if (!onNavigate) return undefined;
    const handler = (e: MessageEvent) => {
      if (!e.data || e.data.type !== 'design-navigate') return;
      const rawTarget = typeof e.data.pathname === 'string' && e.data.pathname
        ? e.data.pathname
        : typeof e.data.path === 'string'
          ? e.data.path
          : null;

      if (!rawTarget) return;

      const normalizedTarget = normalizeDesignNavigationTarget(rawTarget);

      if (!normalizedTarget) return;
      const raw = normalizePageKey(normalizedTarget);
      const match = designPages.find(
        (p) => normalizePageKey(p.page_name) === raw,
      );

      if (match) onNavigate(match.page_name);
    };

    window.addEventListener('message', handler);

    return () => window.removeEventListener('message', handler);
  }, [designPages, onNavigate]);
}
